import React, { useState, useRef, useEffect } from 'react';
import { 
  Bold, 
  Italic, 
  Underline, 
  Palette,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered
} from 'lucide-react';
import { Button } from './ui';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

const colors = [
  { name: 'Default', value: 'inherit' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Gray', value: '#6b7280' },
];

export function RichTextEditor({ value, onChange, placeholder, className = '' }: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const isInitializedRef = useRef(false);

  // Only set initial value once
  useEffect(() => {
    if (editorRef.current && !isInitializedRef.current && value) {
      editorRef.current.innerHTML = value;
      isInitializedRef.current = true;
    }
  }, [value]);

  const execCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    updateValue();
  };

  const handleToolbarClick = (e: React.MouseEvent, command: string, value?: string) => {
    e.preventDefault();
    e.stopPropagation();
    execCommand(command, value);
  };

  const updateValue = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleInput = () => {
    updateValue();
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
    updateValue();
  };

  const isActive = (command: string) => {
    return document.queryCommandState(command);
  };



  return (
    <div className={`border border-gray-200 rounded-2xl overflow-hidden ${className}`}>
      {/* Toolbar */}
      <div 
        className="bg-gray-50 border-b border-gray-200 p-3 flex items-center space-x-2 flex-wrap"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Text Formatting */}
        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => handleToolbarClick(e, 'bold')}
            className={`p-2 h-8 w-8 ${isActive('bold') ? 'bg-primary-100 text-primary-600' : 'text-gray-600 hover:text-gray-900'}`}
          >
            <Bold className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => handleToolbarClick(e, 'italic')}
            className={`p-2 h-8 w-8 ${isActive('italic') ? 'bg-primary-100 text-primary-600' : 'text-gray-600 hover:text-gray-900'}`}
          >
            <Italic className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => handleToolbarClick(e, 'underline')}
            className={`p-2 h-8 w-8 ${isActive('underline') ? 'bg-primary-100 text-primary-600' : 'text-gray-600 hover:text-gray-900'}`}
          >
            <Underline className="w-4 h-4" />
          </Button>
        </div>

        {/* Divider */}
        <div className="w-px h-6 bg-gray-300 mx-2" />

        {/* Text Alignment */}
        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => handleToolbarClick(e, 'justifyLeft')}
            className={`p-2 h-8 w-8 ${isActive('justifyLeft') ? 'bg-primary-100 text-primary-600' : 'text-gray-600 hover:text-gray-900'}`}
          >
            <AlignLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => handleToolbarClick(e, 'justifyCenter')}
            className={`p-2 h-8 w-8 ${isActive('justifyCenter') ? 'bg-primary-100 text-primary-600' : 'text-gray-600 hover:text-gray-900'}`}
          >
            <AlignCenter className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => handleToolbarClick(e, 'justifyRight')}
            className={`p-2 h-8 w-8 ${isActive('justifyRight') ? 'bg-primary-100 text-primary-600' : 'text-gray-600 hover:text-gray-900'}`}
          >
            <AlignRight className="w-4 h-4" />
          </Button>
        </div>

        {/* Divider */}
        <div className="w-px h-6 bg-gray-300 mx-2" />

        {/* Lists */}
        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => handleToolbarClick(e, 'insertUnorderedList')}
            className={`p-2 h-8 w-8 ${isActive('insertUnorderedList') ? 'bg-primary-100 text-primary-600' : 'text-gray-600 hover:text-gray-900'}`}
          >
            <List className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => handleToolbarClick(e, 'insertOrderedList')}
            className={`p-2 h-8 w-8 ${isActive('insertOrderedList') ? 'bg-primary-100 text-primary-600' : 'text-gray-600 hover:text-gray-900'}`}
          >
            <ListOrdered className="w-4 h-4" />
          </Button>
        </div>

        {/* Divider */}
        <div className="w-px h-6 bg-gray-300 mx-2" />

        {/* Color Picker */}
        <div className="relative">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowColorPicker(!showColorPicker);
            }}
            className="p-2 h-8 w-8 text-gray-600 hover:text-gray-900"
          >
            <Palette className="w-4 h-4" />
          </Button>
          
          {showColorPicker && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-2 z-10 min-w-[200px]">
              <div className="grid grid-cols-4 gap-2">
                {colors.map((color) => (
                  <button
                    key={color.value}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (color.value === 'inherit') {
                        // For default color, set to black (default text color)
                        execCommand('foreColor', '#000000');
                      } else {
                        execCommand('foreColor', color.value);
                      }
                      setShowColorPicker(false);
                    }}
                    className="flex flex-col items-center space-y-1 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div 
                      className={`w-6 h-6 rounded-full border border-gray-300 ${
                        color.value === 'inherit' 
                          ? 'bg-gradient-to-br from-gray-100 to-gray-200' 
                          : ''
                      }`}
                      style={{ 
                        backgroundColor: color.value === 'inherit' ? undefined : color.value 
                      }}
                    >
                      {color.value === 'inherit' && (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                        </div>
                      )}
                    </div>
                    <span className="text-xs text-gray-600">{color.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        onBlur={updateValue}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        className="min-h-[120px] p-4 focus:outline-none text-gray-900 font-text"
        style={{ 
          lineHeight: '1.6',
          wordBreak: 'break-word'
        }}
        data-placeholder={placeholder}
      />
    </div>
  );
}
